import { financialClasses } from "utils/patientFormOptions";

export const calculateAge = (birthDate) => {
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

// Whole months between two dates, plus the days left over past the last month
// anniversary. Day-of-month arithmetic has to borrow from the previous month rather
// than assume a length: born on the 31st and asked on the 1st, the leftover depends on
// which month it is.
const monthsAndDaysSince = (birth, today) => {
  let months =
    (today.getFullYear() - birth.getFullYear()) * 12 +
    (today.getMonth() - birth.getMonth());

  if (today.getDate() < birth.getDate()) {
    months--;
  }

  const lastAnniversary = new Date(birth);
  lastAnniversary.setMonth(birth.getMonth() + months);

  const days = Math.floor((today - lastAnniversary) / MILLISECONDS_PER_DAY);

  return { months, days: Math.max(days, 0) };
};

const MONTHS_PER_YEAR = 12;
const ADULT_AGE = 18;

/**
 * Age in the HSE / NHS clinical banner format.
 *
 * The bands come from the HSE age display matrix Eleanor set out on KIN-1809, which
 * follows the NHS CUI patient banner. Two conventions travel with it and both are
 * deliberate: no space between the number and its unit, and the unit always singular.
 * Both exist to keep a banner scannable and to stop a screen reader mangling it — "5mo
 * 3w", never "5 months 3 weeks".
 *
 * | age            | shows      |
 * | -------------- | ---------- |
 * | under 4 weeks  | 14d        |
 * | 4 weeks-2 years| 5mo 21d    |
 * | 2-18 years     | 5y 3mo     |
 * | 18 and over    | 42         |
 *
 * A whole number of years carries no unit: an age with nothing after it is already read
 * as years, and "42y" beside a name is the one band where the suffix earns nothing. The
 * units stay wherever two parts have to be told apart ("5y 3mo").
 *
 * Days rather than weeks below two years, though the standard permits either. This
 * application's reason for showing an infant's age at all is the vaccination schedule,
 * and those intervals are counted in days — "6 months minus 4 days" is a real rule here,
 * so rounding to the nearest week would hide the thing the number is being read for.
 *
 * The matrix also defines minutes under 2 hours and hours under 48. Neither is
 * reachable: `dateOfBirth` is a date with no time, so the first two days are
 * indistinguishable and both render as days.
 *
 * Display only — {@link calculateAge} stays numeric because age thresholds are compared
 * against it (PP registration reasons, vaccine age rules).
 */
export const formatPatientAge = (birthDate) => {
  if (!birthDate) return "-";

  const birth = new Date(birthDate);

  if (Number.isNaN(birth.getTime())) return "-";

  const today = new Date();
  const years = calculateAge(birthDate);

  // A date of birth in the future is a data-entry error, and "-1" reads as a real age.
  if (years < 0) return "-";

  if (years >= ADULT_AGE) return `${years}`;

  const { months, days } = monthsAndDaysSince(birth, today);

  if (months >= 2 * MONTHS_PER_YEAR) {
    const trailingMonths = months % MONTHS_PER_YEAR;

    return trailingMonths > 0 ? `${years}y ${trailingMonths}mo` : `${years}`;
  }

  // Days until there is a whole month to report. The band nominally turns at four weeks,
  // but a 28-day-old is still nought months, and "0mo 28d" is a worse answer than "28d".
  if (months < 1) {
    const totalDays = Math.floor((today - birth) / MILLISECONDS_PER_DAY);

    return `${Math.max(totalDays, 0)}d`;
  }

  return days > 0 ? `${months}mo ${days}d` : `${months}mo`;
};

/**
 * The same age where the surrounding copy expects a unit spelled out — the payment
 * modal reads "(45 years)".
 *
 * A separate function rather than a flag, because the banner format already carries its
 * own units and "(2mo 6d years)" is nonsense.
 */
export const formatPatientAgeWithUnit = (birthDate) => {
  const age = formatPatientAge(birthDate);

  if (age === "-") return age;

  const wholeYears = /^(\d+)y$/.exec(age);

  if (!wholeYears) return age;

  const count = Number(wholeYears[1]);

  return `${count} year${count === 1 ? "" : "s"}`;
};

export const getInitials = (name) => {
  if (!name) return "";
  try {
    return name
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0].toUpperCase())
      .join("");
  } catch {
    return "";
  }
};

export const getPatientClassificationLabel = (financialClass) => {
  if (!financialClass || financialClass === "") return "No classification";
  const option = financialClasses.find((c) => c.value === financialClass);
  return option?.label ?? "No classification";
};

// Ethnicity can reach a CDM/PP review either as a code (patient file / manual
// Select) or as a description enum (round-tripped review, PatientDemographicsDto).
// Normalise any incoming value to the canonical code so the Select — which looks
// up by `g.code` — prefills correctly regardless of the source shape.
export const resolveEthnicityCode = (value, ethnicGroups) => {
  if (!value || !ethnicGroups?.length) return "";

  const byCode = ethnicGroups.find((group) => group.code === value);
  if (byCode) return byCode.code;

  const byDescription = ethnicGroups.find(
    (group) => group.description === value
  );

  return byDescription?.code ?? "";
};

export const formatAddress = (address) => {
  if (!address) return "";
  const { addressLine1, addressLine2, addressLine3, city, county, postCode } =
    address;
  return [addressLine1, addressLine2, addressLine3, city, county, postCode]
    .filter(Boolean)
    .join(", ");
};
